import axios from 'axios';

import { authFetch } from '.';
import { GroupedStatuses, IAgentDrawsAndDeductionsListVM, Status, IAgentDraw } from '../models/agent-draws-and-deductions/agent-draw';

const baseUrl = '/api/Admin/AgentDrawsAndDeductions';

export const IAgentDrawsAndDeductionsAPI = {
    /**
     * Fetch the a list of draws by agent ID with optional filters
     */
    async getDrawsByAgentId(id: number, options: {
    page?: number;
    perPage?: number;
    search?: string;
    approvalStatusFilter?: string;
    drawFinancialStatusFilter?: number | null;
  }): Promise<IAgentDrawsAndDeductionsListVM> {
        try {
            const { page = 1, perPage = 10, search, approvalStatusFilter, drawFinancialStatusFilter } = options;
            const qs = new URLSearchParams({
            page: String(page),
            perPage: String(perPage),
            ...(search?.trim() ? { search: search.trim() } : {}),
            ...(approvalStatusFilter?.trim() ? { approvalStatusFilter: approvalStatusFilter.trim() } : {}),
            ...(drawFinancialStatusFilter != null ? { drawFinancialStatusFilter: String(drawFinancialStatusFilter) } : {}),
            }).toString();
            const response = await authFetch.get(`${baseUrl}/${id}?${qs}`);
            if (response.status < 200 || response.status >= 300) {
                throw new Error(response.statusText);
            }

            return response.data;
        } catch (err) {
            if (axios.isAxiosError(err)) {
                return Promise.reject(err?.response?.data);
            }
            return Promise.reject(err);
        }
    },
    
    async fetchStatuses(): Promise<GroupedStatuses<Status>> {
        const response = await authFetch.get(`${baseUrl}/lookup/draws-and-deductions-enums`);
        return response.data;
    },
    // Update the financial status of a draw
    async updateDrawFinancialStatus(drawId: number, drawFinancialStatusId: number): Promise<IAgentDraw> {
        try {
            const response = await authFetch.put(`${baseUrl}/draw/${drawId}/financial-status`, {
                drawFinancialStatusId,
            });
            if (response.status < 200 || response.status >= 300) {
                throw new Error(response.statusText);
            }
            return response.data;
        } catch (err) {
            if (axios.isAxiosError(err)) {
                return Promise.reject(err?.response?.data);
            }
            return Promise.reject(err);
        }
    },

    // Update the date, amount and memo of a draw
    async updateDraw(drawId: number, payload: { date: string; amount: number; memo?: string; notifyLineLead?: boolean; requireAgentApproval?: boolean; targetDrawId?: number | null }): Promise<IAgentDraw> {
        try {
            const response = await authFetch.put(`${baseUrl}/draw/${drawId}`, {
                date: payload.date,
                amount: payload.amount,
                memo: payload.memo,
                notifyLineLead: payload.notifyLineLead ?? true,
                requireAgentApproval: payload.requireAgentApproval ?? true,
                targetDrawId: payload.targetDrawId ?? null,
            });
            if (response.status < 200 || response.status >= 300) {
                throw new Error(response.statusText);
            }
            return response.data;
        } catch (err) {
            if (axios.isAxiosError(err)) {
                return Promise.reject(err?.response?.data);
            }
            return Promise.reject(err);
        }
    },

    // Create a new draw for an agent
    async createDraw(agentId: number, payload: { date: string; amount: number; memo?: string; drawFinancialStatusId?: number; notifyLineLead?: boolean; requireAgentApproval?: boolean; targetDrawId?: number | null }): Promise<IAgentDraw> {
        try {
            const response = await authFetch.post(`${baseUrl}/${agentId}`, {
                date: payload.date,
                amount: payload.amount,
                memo: payload.memo,
                drawFinancialStatusId: payload.drawFinancialStatusId,
                notifyLineLead: payload.notifyLineLead ?? true,
                requireAgentApproval: payload.requireAgentApproval ?? true,
                targetDrawId: payload.targetDrawId ?? null,
            });
            if (response.status < 200 || response.status >= 300) {
                throw new Error(response.statusText);
            }
            return response.data;
        } catch (err) {
            if (axios.isAxiosError(err)) {
                return Promise.reject(err?.response?.data);
            }
            return Promise.reject(err);
        }
    },

    async sendApprovalEmail(drawId: number): Promise<void> {
        try {
            if (!drawId || Number.isNaN(drawId)) {
                return Promise.reject({ detail: 'Invalid draw identifier' });
            }
            const response = await authFetch.post(`${baseUrl}/draw/${drawId}/send-approval-email`, null);
            if (response.status < 200 || response.status >= 300) {
                throw new Error(response.statusText);
            }
            return;
        } catch (err) {
            if (axios.isAxiosError(err)) {
                return Promise.reject(err?.response?.data);
            }
            return Promise.reject(err);
        }
    },

    async importDraws(importName: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('File', file);
        formData.append('ImportName', importName);

        try {
            const { data } = await authFetch.post(`${baseUrl}/import-draws`, formData);
            return data;
        } catch (error: any) {
            if (error?.response?.data) {
                return error.response.data;
            }
            return { errors: [error?.message || 'Network error. Please try again.'], validationErrors: [] };
        }
    },

    async getImportStatus(importId: number): Promise<ImportStatusResult> {
        const response = await authFetch.get(`${baseUrl}/import-draws/${importId}/status`);
        return response.data;
    },

    previewImportDraws: async (importName: string, file: any): Promise<ImportAgentDrawsPreviewResult> => {
        const formData = new FormData();
        formData.append('ImportName', importName);
        formData.append('File', file);

        try {
            const { data } = await authFetch.post(`${baseUrl}/preview-import-draws`, formData);
            return data;
        } catch (error: any) {
            if (error?.response?.data) {
                // Server already returned a well-formed error payload (e.g. validationErrors)
                return error.response.data;
            }
            return { errors: [error?.message || 'Network error. Please try again.'], validationErrors: [] };
        }
    }
}

type ImportPreviewSuccess = {
    recordCount: number;
    emailRecipientCount: number;
    errors?: never;
    validationErrors?: never;
};

type ImportPreviewFailure = {
    recordCount?: never;
    emailRecipientCount?: never;
    errors: string[];
    validationErrors: string[];
};

type ImportAgentDrawsPreviewResult = ImportPreviewSuccess | ImportPreviewFailure;

export interface ImportAgentDrawsResult {
    importId: number;
    successCount: number;
    emailsSentCount: number;
    emailStatus: string;
    createdDrawIds: number[];
    errors: string[];
    validationErrors: string[];
}

export interface ImportStatusResult {
    importId: number;
    totalRecords: number;
    totalEmailsSent: number;
    status: string;
}